import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/mangaData';

export default function About() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '女神漫画',
    url: 'https://goddessmanga.com',
    description: aboutInfo.introduction,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutInfo.contact.phone,
      email: aboutInfo.contact.email,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 女神漫画 | 品牌介绍、口碑评价与联系方式"
        description={`${aboutInfo.brandName}成立于2020年，是国内领先的女性向漫画阅读平台。${aboutInfo.introduction}`}
        keywords="女神漫画,关于我们,品牌介绍,联系方式,漫画平台,品牌口碑"
        canonicalUrl="https://goddessmanga.com/about"
        schema={{ ...schema, ...faqSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Page Header */}
        <section className="py-10 md:py-14 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-4 mb-3">关于女神漫画</h1>
            <p className="text-foreground-600 max-w-2xl">{aboutInfo.slogan}</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">品牌介绍</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">{aboutInfo.introduction}</p>
              <p className="text-foreground-700 leading-relaxed">{aboutInfo.story}</p>
            </div>

            {/* Mission & Vision */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="p-6 bg-background-100 rounded-lg">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="p-6 bg-background-100 rounded-lg">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
              </div>
            </div>

            {/* Values */}
            <div className="mb-12">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">核心价值观</h3>
              <div className="space-y-3">
                {aboutInfo.values.map((value) => (
                  <div key={value} className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <i className="ri-check-double-line text-primary-500"></i>
                    </div>
                    <span className="text-sm text-foreground-700">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Achievements */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 text-center mb-10">平台数据</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.achievements.map((item) => (
                <div key={item.label} className="text-center p-6 bg-background-50 rounded-lg">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-2">{item.number}</p>
                  <p className="text-sm text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 text-center mb-10">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((item) => (
                <div key={item.name} className="p-5 bg-background-100 rounded-lg">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{item.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-700">{item.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 text-center mb-10">常见问题</h2>
            <div className="space-y-3">
              {aboutInfo.faq.map((item, index) => (
                <details key={index} className="group bg-background-50 rounded-lg overflow-hidden">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between gap-3">
                    <h3 className="text-sm font-medium text-foreground-800">{item.question}</h3>
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 text-center mb-10">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="p-5 bg-background-100 rounded-lg flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</p>
                  <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap transition-colors">
                    {aboutInfo.contact.email}
                  </a>
                </div>
              </div>
              <div className="p-5 bg-background-100 rounded-lg flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-accent-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800 mb-1">微信公众号</p>
                  <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                </div>
              </div>
              <div className="p-5 bg-background-100 rounded-lg flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-secondary-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800 mb-1">公司地址</p>
                  <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                </div>
              </div>
              <div className="p-5 bg-background-100 rounded-lg flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-phone-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800 mb-1">客服电话</p>
                  <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                </div>
              </div>
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/"
                className="px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                返回首页开始阅读
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}